import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Link, TextField, Typography } from '@mui/material';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import { sendPasswordResetEmail } from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import { auth } from '../../firebaseConfig';
import { sendPasswordResetEmailProfessional } from '../../graphqlService';
import AuthLayout from './AuthLayout';
import AuthStatus from './AuthStatus';
import { authMonitoringService } from '../../services/authMonitoringService';
import { useAuth } from '../../context/AuthContext';
import { findRateLimitMessage, rateLimitMessageFromError } from '../../utils/rateLimitMessage';
import { describeAuthError, getAuthErrorCode } from './authErrors';
import { normalizeEmail, validateEmail } from './authForm';
import { backLinkFor } from './authLinks';

interface Problem {
  severity: 'warning' | 'error';
  message: string;
}

/** Matches the API's reset-link lifetime (secureTokenUtils: 1 hour). */
const LINK_LIFETIME = '1 hour';

const ForgotPassword: React.FC = () => {
  // Signed-in people arrive here from Profile's "Change password": the page
  // is theirs too, so it speaks to them and leads back to their profile.
  const { user } = useAuth();
  const signedIn = Boolean(user);
  const accountEmail = user?.email || '';
  const back = backLinkFor(signedIn);

  const [email, setEmail] = useState(accountEmail);
  const [emailError, setEmailError] = useState<string | undefined>();
  const [problem, setProblem] = useState<Problem | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);

  // Sign-in state can finish loading after the first render (a refresh, a
  // typed-in URL). Fill the address in then, once, unless something has
  // already been typed.
  const prefilledRef = useRef(Boolean(accountEmail));
  useEffect(() => {
    if (accountEmail && !prefilledRef.current) {
      prefilledRef.current = true;
      setEmail((current) => current || accountEmail);
    }
  }, [accountEmail]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (loading) return;
    setProblem(null);

    const invalid = validateEmail(email);
    setEmailError(invalid);
    if (invalid) {
      emailRef.current?.focus();
      return;
    }

    const normalizedEmail = normalizeEmail(email);
    setLoading(true);

    try {
      // Our backend sends the reset email (it generates its own secure token)
      // and enforces the per-address and per-connection email limits.
      let result: { success?: boolean | null; error?: string | null; message?: string | null } | null = null;
      let backendError: unknown = null;
      try {
        result = await sendPasswordResetEmailProfessional(normalizedEmail, '');
      } catch (professionalEmailError) {
        backendError = professionalEmailError;
      }

      // A rate limit is an answer, not an outage: falling back to Firebase here
      // sent the email the limit had just refused.
      const rateLimited = backendError
        ? rateLimitMessageFromError(backendError)
        : findRateLimitMessage(result?.error, result?.message);
      if (rateLimited) {
        setProblem({ severity: 'warning', message: rateLimited });
        return;
      }

      if (result?.success) {
        setSentTo(normalizedEmail);
        return;
      }

      // Unreachable, a 5xx, or the backend's own "Unable to process request":
      // the only failures its reset mutation reports besides the rate limit.
      console.warn(
        'Professional email service failed, falling back to Firebase:',
        backendError ?? result?.error
      );
      await sendPasswordResetEmail(auth, normalizedEmail);
      setSentTo(normalizedEmail);
    } catch (error: unknown) {
      const firebaseError = error as FirebaseError;
      authMonitoringService.logLoginFailure(normalizedEmail, firebaseError, 'password_reset');

      // Saying "no account" here would tell anyone which addresses have
      // accounts, so a missing account reads like a sent link.
      if (getAuthErrorCode(error) === 'auth/user-not-found') {
        setSentTo(normalizedEmail);
        return;
      }

      const copy = describeAuthError(error, 'reset-request');
      if (copy.field === 'email') {
        setEmailError(copy.message);
        emailRef.current?.focus();
      } else {
        setProblem({ severity: copy.severity, message: copy.message });
      }
    } finally {
      setLoading(false);
    }
  };

  if (sentTo) {
    // For the signed-in account's own address there is no doubt it exists.
    const ownAddress = signedIn && sentTo === normalizeEmail(accountEmail);
    return (
      <AuthLayout>
        <AuthStatus
          tone="success"
          icon={<MarkEmailReadOutlinedIcon />}
          title="Check your email"
          description={
            ownAddress
              ? `We sent a link to ${sentTo} to choose a new password. The link works for ${LINK_LIFETIME}.`
              : `If an account exists for ${sentTo}, we've sent a link to choose a new password. The link works for ${LINK_LIFETIME}.`
          }
          primaryAction={back}
          secondaryAction={{ label: 'Use a different email', onClick: () => setSentTo(null) }}
          footer={
            <Typography variant="body2" color="text.secondary">
              Don't see it? Check your spam folder. It can take a few minutes to arrive.
            </Typography>
          }
          focusOnMount
        />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={signedIn ? 'Change your password' : 'Forgot your password?'}
      subtitle={
        signedIn
          ? "We'll email you a link to choose a new password."
          : "Enter the email address for your account and we'll send you a link to choose a new password."
      }
    >
      {problem && (
        <Alert severity={problem.severity} sx={{ mb: 3 }}>
          {problem.message}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: 'grid', gap: 2.5 }}>
        <TextField
          inputRef={emailRef}
          id="email"
          name="email"
          type="email"
          label="Email address"
          autoComplete="email"
          autoFocus
          required
          InputLabelProps={{ required: false }}
          fullWidth
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            if (emailError) setEmailError(undefined);
          }}
          error={Boolean(emailError)}
          helperText={emailError}
        />

        <Button type="submit" fullWidth variant="contained" size="large" disabled={loading}>
          {loading ? (
            <>
              <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} aria-hidden="true" />
              Sending link…
            </>
          ) : (
            'Send reset link'
          )}
        </Button>

        <Box sx={{ textAlign: 'center', mt: 1 }}>
          <Link
            component={RouterLink}
            to={back.to}
            variant="body2"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontWeight: 600 }}
          >
            <ArrowBackOutlinedIcon sx={{ fontSize: 16 }} aria-hidden="true" />
            {back.label}
          </Link>
        </Box>
      </Box>
    </AuthLayout>
  );
};

export default ForgotPassword;
